<script lang="ts" module>
	import type { MarkdownTokenList } from '$global/components/Markdown';

	export interface PdpDetailsRow {
		label: string;
		value: string | boolean;
		link?: string;
		scaleImageIndex?: number;
		markdown?: boolean;
		/**
		 * With `markdown`: the value parsed on the server
		 * (withDetailsRowTokensServer), which is what is rendered.
		 */
		valueTokens?: MarkdownTokenList;
	}
</script>

<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { SizeIcon } from '$global/assets/icons/SizeIcon';
	import { loadLightbox } from '$global/components/Lightbox/utils/loadLightbox/loadLightbox';
	import { Link } from '$global/components/Link';
	import { LinkButton } from '$global/components/LinkButton';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { toKebabCase } from '$global/utils/toKebabCase';

	let {
		dataCy,
		rows = [],
		selectedItemIndex = $bindable(),
		isLightboxOpen = $bindable(),
		href,
		includeLightbox = true,
		onSizingGuide,
		...rest
	}: Props = $props();

	interface Props {
		dataCy: string;
		rows?: PdpDetailsRow[];
		selectedItemIndex: number;
		isLightboxOpen: boolean;
		href: string | null | undefined;
		// Without a lightbox (inside a dialog), the sizing guide only selects
		// the scale image in the gallery.
		includeLightbox?: boolean;
		// Runs once the sizing guide has selected the scale image, e.g. to bring
		// the gallery back into view.
		onSizingGuide?: () => void;
		class?: string;
	}

	let dataCyPrefix = $derived(`${dataCy}-details`);

	// The gallery's lightbox loads on demand: warm it up before the sizing
	// guide opens it.
	const prefetchLightbox = () => {
		loadLightbox().catch(() => {
			// The gallery retries when it opens.
		});
	};
</script>

<div
	data-cy={dataCyPrefix}
	class={twMerge('grid grid-cols-1 gap-3', rest.class)}
>
	{#each rows as item, index}
		{@const scaleImageIndex = item.scaleImageIndex}
		<div class="flex items-start">
			<div class="w-[9rem]">
				<Txt
					class="leading-[1.5rem]"
					dataCy={`${dataCyPrefix}-${toKebabCase(item.label)}-label`}
					variant="label2">{item.label}:</Txt
				>
			</div>
			<div class="max-w-[calc(100%-9rem)] flex-grow">
				{#if item.link}
					<Link href={item.link} class="underline">
						<Txt
							dataCy={`${dataCyPrefix}-${toKebabCase(item.label)}-value-link`}
							variant="body1"
							class="text-gray-700">{item.value}</Txt
						>
					</Link>
				{:else if item.markdown}
					<Markdown
						class="[&>p]:mb-0"
						{dataCy}
						source={item.valueTokens ?? []}
					/>
				{:else}
					<Txt
						dataCy={`${dataCyPrefix}-${toKebabCase(item.label)}-value`}
						variant="body1"
						class="text-gray-700">{item.value}</Txt
					>
				{/if}

				{#if scaleImageIndex !== undefined}
					<button
						data-cy={`${dataCy}-sizing-guide`}
						onpointerenter={includeLightbox ? prefetchLightbox : undefined}
						onfocus={includeLightbox ? prefetchLightbox : undefined}
						onclick={() => {
							selectedItemIndex = scaleImageIndex;
							isLightboxOpen = includeLightbox;
							onSizingGuide?.();
						}}
					>
						<Txt
							variant="body3"
							component="span"
							class="flex items-center text-gray-500 underline"
						>
							<SizeIcon color="gray-500" class="mr-1 w-4" />
							Sizing Guide
						</Txt>
					</button>
				{/if}
			</div>
		</div>

		{#if index < rows.length - 1}
			<hr />
		{/if}
	{/each}

	{#if href}
		<LinkButton
			size="md"
			dataCy={`${dataCyPrefix}-drop-info`}
			variant="secondary"
			fullWidth
			{href}
			class="mt-8"
		>
			Drop info
		</LinkButton>
	{/if}
</div>
